import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { loadUiComponent } from "../graphql/queries/loadUiComponent";

export const normalizeEmailPreferences = (emailPreferences = {}) => {
  const keys = [
    "instantWinner",
    "instantLooser",
    "sweepstakeEntry",
    "sweepstakeWinner",
    "leaderboardWinner",
  ];

  const normalized = { ...emailPreferences };

  keys.forEach((key) => {
    const value = normalized[key];

    if (value === null || value === undefined || typeof value !== "object") {
      normalized[key] = { enabled: false };
      return;
    }

    const cleaned = { ...value };

    // Ensure enabled is a boolean; default to false if missing/invalid
    if (typeof cleaned.enabled !== "boolean") {
      cleaned.enabled = false;
    }

    // If not enabled, drop definitionKey entirely to avoid nulls
    if (!cleaned.enabled) {
      delete cleaned.definitionKey;
    }

    normalized[key] = cleaned;
  });

  return normalized;
};

const useCampaignEmailConfiguration = (currentCampaignTypeConfig) => {
  const getCampaignEmailConfigurationSlug = () => {
    const campaignEmailConfigurationStep = currentCampaignTypeConfig?.steps
      ?.find((step) => step.slug === "generic-setup")
      ?.values?.find((value) => value.type === "genericCampaignEmailConfiguration");

    if (campaignEmailConfigurationStep) {
      return campaignEmailConfigurationStep.definition;
    }
    return undefined;
  };

  const campaignEmailConfigurationSlug = getCampaignEmailConfigurationSlug();

  const { data } = useQuery(loadUiComponent, {
    variables: { slug: campaignEmailConfigurationSlug },
    skip: !campaignEmailConfigurationSlug,
  });

  const campaignEmailConfigurationUiComponent = data?.loadUiComponent;

  const hasGenericCampaignEmailConfiguration = useMemo(() => {
    const campaignEmailConfigurationStep = currentCampaignTypeConfig?.steps
      ?.find((step) => step.slug === "generic-setup")
      ?.values?.find((value) => value.type === "genericCampaignEmailConfiguration");

    return Boolean(campaignEmailConfigurationStep);
  }, [currentCampaignTypeConfig]);

  const campaignEmailConfigurationSchemas = useMemo(
    () => ({
      inputSchema: campaignEmailConfigurationUiComponent?.inputSchema,
      uiSchema: campaignEmailConfigurationUiComponent?.uiSchema,
    }),
    [campaignEmailConfigurationUiComponent]
  );

  return {
    getCampaignEmailConfigurationSlug,
    campaignEmailConfigurationSlug,
    hasGenericCampaignEmailConfiguration,
    campaignEmailConfigurationQueryData: data?.loadUiComponent,
    campaignEmailConfigurationSchemas,
  };
};

export default useCampaignEmailConfiguration;
